import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import {
  WHDropdownModule,
  WHLayoutConfigService,
  WHRouteZeroENUM,
  WHAutocompleteScrollComponent,
  IWHBomNodeDTO,
  WHIconENUM,
  WHFeatureKeyENUM,
  WHLoginDataService,
} from '@workheld/workheld-shared-lib';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MaintenanceNotificationsStore } from '../entry-maintenance-notifications.store';
import {
  MetadataEnum,
  WHEnumDataService,
} from 'src/app/app-services-helper/w-h-enum-helper.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatTimepickerModule } from '@angular/material/timepicker';
import moment from 'moment';
import { WHFileDragNDropComponent } from 'src/app/app-shared/components/files-dnd/w-h-files-dnd.component';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  EquipmentAsyncService,
  SimpleEquipmentPageable,
} from 'src/app/app-services-async/equipment-async.service';
import { BehaviorSubject, Observable, Subscription, tap } from 'rxjs';
import { AppSharedModule } from 'src/app/app-shared/app-shared.module';
import { FormReferenceService } from 'src/app/app-services-helper/form-reference.service';
import { EquipmentStore } from 'src/app/app-modules-pages/equipment/equipment.store';
import { EquipmentStructureComponent } from 'src/app/app-modules-pages/equipment/equipment-detail/equipment-structure/equipment-structure.component';
import { WHButtonComponent } from 'src/app/app-shared/components/buttons/w-h-button/w-h-button.component';

@UntilDestroy()
@Component({
  selector: 'app-entry-maintenance-notification-add',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    WHDropdownModule,
    MatDatepickerModule,
    MatTimepickerModule,
    WHFileDragNDropComponent,
    AppSharedModule,
    WHAutocompleteScrollComponent,
    EquipmentStructureComponent,
    WHButtonComponent,
  ],
  template: `
    @let maintenanceNotificationTypeFormControl =
      maintenanceFormGroup.get('maintenanceNotificationType');
    @let categoryFormControl = maintenanceFormGroup.get('category');
    @let errorCodeFormControl = maintenanceFormGroup.get('errorCode');
    @let costCenterFormControl = maintenanceFormGroup.get('costCenter');
    @let loading = store.isLoading();

    <div class="detail-card-header p--12">
      <div class="flex--r-c flex--wrap gap--12">
        <mat-icon>warning</mat-icon>
        {{ 'app.ui.createMaintenance.label' | translate }}
      </div>
    </div>

    <div class="maintenance-body flex--col gap--12 p--16">
      <mat-button-toggle-group
        data-cy="operational-area-toggle"
        [value]="operationalArea()"
        (change)="handleOperationalAreaChange($event)"
        class="operational-area-buttons"
      >
        @for (option of operationalAreaOptions(); track option.id) {
          <mat-button-toggle
            [value]="option.id"
            [attr.data-cy]="'operational-area-' + option.id.toLowerCase()"
            [matTooltip]="option.translateKey | translate"
          >
            <span class="toggle-content">
              <span class="toggle-label">{{
                option.translateKey | translate
              }}</span>
              @if (predefinedIcons[option.id]; as icon) {
                <mat-icon>{{ icon }}</mat-icon>
              }
            </span>
          </mat-button-toggle>
        }
      </mat-button-toggle-group>
      <form [formGroup]="maintenanceFormGroup" class="flex--col gap--12">
        <div class="flex--r-c flex--wrap gap--12">
          <lib-w-h-dropdown
            class="flex-1"
            data-cy="maintenance-notification-type-dropdown"
            [required]="true"
            [tabIndex]="1"
            [valueLabel]="'entry.defect.model.defecttype'"
            [itemKeys]="['translateKey']"
            [valueFormControl]="maintenanceNotificationTypeFormControl"
            [dropdownItemDomList]="defectTypeDropdownList()"
          >
          </lib-w-h-dropdown>

          <lib-w-h-dropdown
            class="flex-1"
            data-cy="defect-category-dropdown"
            [required]="true"
            [tabIndex]="2"
            [valueLabel]="'entry.defect.model.defectcategory'"
            [itemKeys]="['translateKey']"
            [valueFormControl]="categoryFormControl"
            [dropdownItemDomList]="$ENUMS().MAINT_NOTIF_CATEGORY"
          >
          </lib-w-h-dropdown>
        </div>

        @if (isBomNodeLevelEnabled()) {
          <div class="flex--r-c gap--12" data-cy="bom-node-selection">
            <div
              class="bom-node-picker-container flex--col flex-1"
              [ngClass]="{ active: selectedEquipment() }"
            >
              <div class="bom-node-picker-header flex--r-c-sb">
                <span class="flex--r-c min-w-0 gap--8 flex-1">
                  <w-h-autocomplete-scroll
                    class="flex-1"
                    data-cy="equipment-dropdown"
                    [data$]="equipmentData$"
                    [required]="true"
                    [loading$]="equipmentDropDownLoading$"
                    [imageUrlKey]="'thumbnailURL'"
                    [labelKey]="'nav.equipment.root'"
                    [tabIndex]="3"
                    [resetValue]="
                      maintenanceFormGroup.get('bomNodeId')?.value == null
                    "
                    (onItemSelect)="setEquipment($event)"
                    (onScrollEvent)="handleScrollEvent($event)"
                  ></w-h-autocomplete-scroll>

                  @if (selectedEquipment() && selectedBomNode()) {
                    <div class="flex--r-c min-w-0 gap--8 flex-1">
                      <span class="connecting-line"></span>
                      <app-w-h-chip-list
                        class="flex-1 min-w-0"
                        [data]="[{ name: selectedBomNode().name }]"
                        [icon]="nodeIcon()"
                        [tooltipKeys]="['name']"
                        [isStacked]="false"
                        [titleKey]="'name'"
                        [isRemoveButtonVisible]="true"
                        (handleRemoveChip)="clearBomNodeSelection()"
                      ></app-w-h-chip-list>
                    </div>
                  }
                </span>
              </div>
              @if (selectedEquipment()) {
                <div
                  class="bom-node-tree-wrapper"
                  [ngClass]="{ disabled: !showBomNodeTree() }"
                >
                  <app-equipment-structure
                    [rootBomNodeId]="equipmentStore.currentNode()?.id"
                    [hasSkillManagementEnabled]="false"
                    [includeDecommissioned]="false"
                    [resetSelectedNode]="!selectedBomNode()"
                    [showNodeId]="isShowBomNodeIdEnabled()"
                    [isModalView]="true"
                    [showInactiveChildren]="false"
                    [enableSearch]="true"
                    (handleNodeMenuClick)="handleBomNodeSelect($event)"
                  ></app-equipment-structure>
                </div>
                <div
                  class="action-row flex--r-c-fe gap--8 p--8"
                  [ngClass]="{ disabled: !showBomNodeTree() }"
                >
                  <w-h-button
                    [text]="'app.assign.label' | translate | uppercase"
                    matIcon="check"
                    color="accent"
                    type="flat"
                    [disabled]="!pendingBomNode()"
                    [dataCy]="'assign-bom-node-button'"
                    (clicked)="confirmBomNodeSelection()"
                  />
                </div>
              }
            </div>
          </div>
        } @else {
          <w-h-autocomplete-scroll
            class="flex-1"
            data-cy="equipment-dropdown"
            [data$]="equipmentData$"
            [required]="true"
            [loading$]="equipmentDropDownLoading$"
            [imageUrlKey]="'thumbnailURL'"
            [labelKey]="'nav.equipment.root'"
            [tabIndex]="3"
            [resetValue]="maintenanceFormGroup.get('bomNodeId')?.value == null"
            (onItemSelect)="setEquipment($event)"
            (onScrollEvent)="handleScrollEvent($event)"
          ></w-h-autocomplete-scroll>
        }

        <div class="flex--r-c flex--wrap gap--12">
          <lib-w-h-dropdown
            class="flex-1"
            data-cy="error-code-dropdown"
            [required]="true"
            [tabIndex]="4"
            [valueLabel]="'entry.defect.model.errorCode'"
            [itemKeys]="['translateKey']"
            [valueFormControl]="errorCodeFormControl"
            [dropdownItemDomList]="defectErrorCodeDropdownList()"
          >
          </lib-w-h-dropdown>

          <mat-form-field class="flex-1" data-cy="contact-person-field">
            <mat-label>{{
              'workstep.model.contactperson' | translate
            }}</mat-label>
            <input
              matInput
              data-cy="contact-person-input"
              placeholder="{{ 'workstep.model.contactperson' | translate }}"
              formControlName="contactPerson"
              [tabIndex]="5"
              autocomplete="off"
            />
          </mat-form-field>
        </div>

        <div class="flex--r-fs gap--12">
          <mat-form-field class="flex-1" data-cy="title-field">
            <mat-label>{{ 'app.ui.symptomTitle.label' | translate }}</mat-label>
            <input
              matInput
              data-cy="title-input"
              placeholder="{{ 'app.ui.symptomTitle.label' | translate }}"
              formControlName="title"
              [tabIndex]="6"
              required
              autocomplete="off"
            />
          </mat-form-field>
          @if (maintenanceFormGroup.get('completionDate').enabled) {
            <div
              class="flex--r-c gap--12 flex-1"
              data-cy="completion-date-container"
            >
              <mat-form-field class="flex-2" data-cy="completion-date-field">
                <mat-label>{{ 'app.ui.dueDate.label' | translate }}</mat-label>
                <input
                  matInput
                  data-cy="completion-date-input"
                  [matDatepicker]="picker"
                  formControlName="completionDate"
                  [tabIndex]="7"
                />
                <mat-datepicker-toggle
                  matIconSuffix
                  [for]="picker"
                ></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
              </mat-form-field>

              <mat-form-field class="flex-1" data-cy="completion-time-field">
                <mat-label>{{
                  'entry.defect.model.date' | translate
                }}</mat-label>
                <input
                  matInput
                  data-cy="completion-time-input"
                  [matTimepicker]="timepicker"
                  formControlName="completionTime"
                  [tabIndex]="8"
                />
                <mat-timepicker #timepicker />
                <mat-timepicker-toggle [for]="timepicker" matSuffix />
              </mat-form-field>
            </div>
          }
        </div>

        <div class="flex--r-c flex-1">
          <mat-form-field class="flex-1" data-cy="description-field">
            <mat-label>{{
              'app.ui.symptomDescription.label' | translate
            }}</mat-label>
            <textarea
              matInput
              data-cy="description-input"
              [rows]="3"
              formControlName="comment"
              [tabIndex]="9"
            >
            </textarea>
          </mat-form-field>
        </div>

        @if (costCenterDropdownList().length > 0) {
          <div class="flex--r-c flex--wrap gap--12">
            <lib-w-h-dropdown
              class="flex-1"
              data-cy="cost-center-dropdown"
              [tabIndex]="10"
              [valueLabel]="'app.ui.costCenter.label'"
              [itemKeys]="['translateKey']"
              [valueFormControl]="costCenterFormControl"
              [dropdownItemDomList]="costCenterDropdownList()"
            >
            </lib-w-h-dropdown>
          </div>
        }
      </form>

      <div class="wh-assignment-box">
        <w-h-file-dnd-zone
          [type]="'MAINTENANCE_NOTIFICATION'"
          [uploadSuccessNotification]="'nodefile.ui.uploadsuccess.notification'"
          [deleteSuccessNotification]="'nodefile.ui.deletesuccess.notification'"
          (filesToUpload)="onFilesToUpload($event)"
        ></w-h-file-dnd-zone>
      </div>
    </div>

    <div
      class="footer-actions flex--r-fe-fe p--16 gap--12"
      style="border-top: 1px solid #e8e8e8"
    >
      <w-h-button
        [text]="'app.cancel.label' | translate | uppercase"
        matIcon="close"
        [disabled]="loading"
        dataCy="cancel-button"
        (clicked)="handleClose()"
      />

      <w-h-button
        [text]="'app.save.label' | translate | uppercase"
        matIcon="save"
        color="accent"
        [isLoading]="loading"
        type="flat"
        [disabled]="loading || maintenanceFormGroup.invalid"
        dataCy="save-button"
        (clicked)="handleSave()"
      />
    </div>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }
    .detail-card-header {
      color: var(--primary-color-50);
      font-size: 18px;
      flex: 0 0 auto;
    }
    .connecting-line {
      height: 2px;
      width: 17px;
      border: none;
      border-top: 3px dotted gray;
    }

    .maintenance-body {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
    }

    .footer-actions {
      flex: 0 0 auto;
    }

    .operational-area-buttons {
      align-self: flex-start;
      overflow: unset !important;
    }

    :host::ng-deep {
      .operational-area-buttons .mat-button-toggle {
        min-width: 140px;
        max-width: 220px;
      }

      .mat-button-toggle-label-content {
        display: flex;
        align-items: center;
        gap: 8px;
        overflow: hidden;
      }
    }

    .toggle-content {
      display: flex;
      align-items: center;
      gap: 8px;
      overflow: hidden;
    }

    .toggle-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .bom-node-picker-container {
      width: 100%;
      &.active {
        .bom-node-picker-header {
          padding: 4px 8px;
          border-bottom: 1px solid #e8e8e8;
        }

        border-radius: 4px;
        border: 1px solid #e8e8e8;
        background: #fafafa;
      }
    }

    .action-row {
      border-top: 1px solid #e8e8e8;
      &.disabled {
        display: none;
      }
    }

    .bom-node-tree-wrapper {
      max-height: 50vh;
      height: 50vh;
      overflow-y: auto;
      position: relative;
      transition: 0.3s ease-in-out;

      &.disabled {
        max-height: 0px;
      }
    }
  `,
})
export class EntryMaintenanceNotificationAddComponent implements OnDestroy {
  store = inject(MaintenanceNotificationsStore);
  $ENUMS = inject(WHEnumDataService).$ENUMS;
  currentUser = inject(WHLayoutConfigService)?.layoutLoggedInUserDOM$?.value;
  loginDataService = inject(WHLoginDataService);
  matDialogRef = inject(MatDialogRef<EntryMaintenanceNotificationAddComponent>);
  router = inject(Router);
  formReferenceService = inject(FormReferenceService);
  equipmentAsyncService = inject(EquipmentAsyncService);
  equipmentStore = inject(EquipmentStore);
  isBomNodeLevelEnabled = signal(false);
  nodeIcon = computed(() => {
    return this.selectedBomNode()?.parentNodeId != undefined
      ? WHIconENUM.BomIcon
      : WHIconENUM.EquipmentIcon;
  });

  /** Icon map for predefined operational area options */
  readonly predefinedIcons: Record<string, string> = {
    PROD: 'precision_manufacturing',
    INFRA: 'business',
  };

  /** Operational area options driven by tenant-configurable enum */
  operationalAreaOptions = computed(() => {
    const enumOptions = this.$ENUMS().MAINTENANCE_NOTIFICATION_DOMAIN;

    if (enumOptions?.length > 0) {
      return enumOptions;
    }
    return [
      { id: 'PROD', translateKey: 'app.ui.production.info.label' },
      { id: 'INFRA', translateKey: 'app.ui.plantInfrastructure.info.label' },
    ];
  });

  operationalArea = signal<string>('');

  private allOperationalAreaPrefixes = computed(() =>
    this.operationalAreaOptions().map((opt) => opt.id.toLowerCase()),
  );

  defectTypes = signal([]);
  selectedEquipment = signal<{
    id: string;
    bomNodeId: string;
    name: string;
  } | null>(null);
  selectedBomNode = signal<IWHBomNodeDTO | null>(null);
  showBomNodeTree = signal(false);
  pendingBomNode = signal<IWHBomNodeDTO | null>(null);
  defectTypeDropdownList = computed(() => {
    const area = this.operationalArea().toLowerCase();
    const allPrefixes = this.allOperationalAreaPrefixes();
    return this.$ENUMS().MAINTENANCE_NOTIFICATION_TYPE.filter((item) => {
      const itemId = item.id.toLowerCase();
      if (itemId.startsWith(area)) return true;
      return !allPrefixes.some((prefix) => itemId.startsWith(prefix));
    });
  });

  defectErrorCodeDropdownList = computed(() => {
    const area = this.operationalArea().toLowerCase();
    const allPrefixes = this.allOperationalAreaPrefixes();
    return this.$ENUMS().MAINT_NOTIF_ERROR_CODE.filter((item) => {
      const itemId = item.id.toLowerCase();
      if (itemId.startsWith(area)) return true;
      return !allPrefixes.some((prefix) => itemId.startsWith(prefix));
    });
  });

  /** Optional cost center, driven by the tenant-configurable COST_CENTER enum */
  costCenterDropdownList = computed<MetadataEnum[]>(
    () => this.$ENUMS().MAINT_NOTIF_COST_CENTER,
  );
  equipmentData$: Observable<SimpleEquipmentPageable> = new Observable(null);
  equipmentDropDownLoading$ = new BehaviorSubject(false);

  maintenanceFormGroup = new FormGroup({
    maintenanceNotificationType: new FormControl(null, Validators.required),
    bomNodeId: new FormControl(null, Validators.required),
    category: new FormControl(null, Validators.required),
    errorCode: new FormControl(null, Validators.required),
    costCenter: new FormControl(null),
    contactPerson: new FormControl(null),
    completionDate: new FormControl(moment()),
    completionTime: new FormControl(moment()),
    clientCreationDate: new FormControl(moment()),
    title: new FormControl(null, Validators.required),
    comment: new FormControl(null),
  });
  filesToUpload: File[] = [];
  isShowBomNodeIdEnabled = signal(false);
  subscription = new Subscription();

  constructor() {
    this.maintenanceFormGroup.get('completionDate')?.disable();

    effect(() => {
      const options = this.operationalAreaOptions();
      if (options.length > 0) {
        this.setOperationalArea(options[0].id);
      }
    });

    // control completion date and time enable/disable based on defect type
    this.maintenanceFormGroup
      .get('maintenanceNotificationType')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (!value?.id?.toLowerCase().includes('repair')) {
          this.maintenanceFormGroup.get('completionDate')?.disable();
          this.maintenanceFormGroup.get('completionTime')?.disable();
        } else {
          this.maintenanceFormGroup.get('completionDate')?.enable();
          this.maintenanceFormGroup.get('completionTime')?.enable();
        }
      });

    effect(() => {
      this.subscription.add(
        this.equipmentAsyncService.getEquipmentSimpleList({
          operationalArea: this.operationalArea(),
          sort: 'name',
          sortMode: 'ASC',
          page: 0,
          term: null,
        }),
      );
    });

    const { equipmentSimpleDropdownList$, equipmentDropdownloading$ } =
      this.equipmentAsyncService.equipmentDropdown;

    this.equipmentData$ = equipmentSimpleDropdownList$;
    this.equipmentDropDownLoading$ = equipmentDropdownloading$;

    this.loginDataService.featureConfigMap$
      .pipe(untilDestroyed(this))
      .subscribe((featureConfigMap: Map<string, boolean>) => {
        this.isBomNodeLevelEnabled.set(
          featureConfigMap.get(
            WHFeatureKeyENUM.MAINTENANCE_NOTIFICATIONS_BOMNODE_LEVEL,
          ) ?? false,
        );

        this.isShowBomNodeIdEnabled.set(
          featureConfigMap.get(
            WHFeatureKeyENUM.EQUIPMENT_STRUCTURE_SHOW_BOMNODE_ID,
          ) ?? false,
        );
      });
  }

  handleOperationalAreaChange(event) {
    this.setOperationalArea(event.value);
  }

  private setOperationalArea(value: string) {
    this.operationalArea.set(value);
    this.maintenanceFormGroup.get('bomNodeId')?.reset();
    this.maintenanceFormGroup.get('maintenanceNotificationType')?.reset();
    this.maintenanceFormGroup.get('errorCode')?.reset();
    this.selectedEquipment.set(null);
    this.selectedBomNode.set(null);
    this.showBomNodeTree.set(false);
    this.pendingBomNode.set(null);
  }

  onFilesToUpload(files: File[]) {
    this.filesToUpload = files;
  }

  setEquipment(equipment) {
    this.maintenanceFormGroup.get('bomNodeId')?.setValue(equipment?.bomNodeId);
    this.selectedEquipment.set(
      equipment
        ? {
            id: equipment.id,
            bomNodeId: equipment.bomNodeId,
            name: equipment.name,
          }
        : null,
    );

    if (!this.isBomNodeLevelEnabled()) {
      return;
    }

    if (!this.selectedEquipment()) {
      this.equipmentStore.resetStore();
      return;
    }
    this.equipmentStore.loadEquipment({
      bomNodeId: equipment.bomNodeId,
    });

    this.selectedBomNode.set(null);
    this.showBomNodeTree.set(true);
    this.pendingBomNode.set(null);
  }

  handleBomNodeSelect(event: { node: IWHBomNodeDTO }) {
    this.pendingBomNode.set(event.node);
  }

  confirmBomNodeSelection() {
    const node = this.pendingBomNode();
    if (node) {
      this.maintenanceFormGroup.get('bomNodeId')?.setValue(node.id);
      this.selectedBomNode.set(node);
    }
    this.showBomNodeTree.set(false);
    this.pendingBomNode.set(null);
  }

  clearBomNodeSelection() {
    const equipment = this.selectedEquipment();
    this.maintenanceFormGroup.get('bomNodeId')?.setValue(equipment?.bomNodeId);
    this.selectedBomNode.set(null);
    this.showBomNodeTree.set(true);
  }

  ngOnDestroy(): void {
    if (this.isBomNodeLevelEnabled()) {
      this.equipmentStore.resetStore();
    }

    this.subscription.unsubscribe();
  }

  handleClose() {
    if (this.maintenanceFormGroup.dirty) {
      const ref = this.formReferenceService.createDialog();
      ref
        .pipe(
          untilDestroyed(this),
          tap((canDiscard) => {
            if (canDiscard) {
              this.matDialogRef.close();
            }
          }),
        )
        .subscribe();
    } else {
      this.matDialogRef.close(false);
    }
  }

  handleScrollEvent(event: { term: string; page: number }) {
    if (event.page === 0 && !event.term) return;
    this.subscription.add(
      this.equipmentAsyncService.getEquipmentSimpleList({
        page: event.page,
        term: event.term || null,
        operationalArea: this.operationalArea(),
        sort: 'name',
        sortMode: 'ASC',
      }),
    );
  }

  handleSave() {
    if (this.maintenanceFormGroup.valid) {
      const formValue = this.maintenanceFormGroup.value;

      const date = formValue.completionDate;
      const time = formValue.completionTime;

      if (date && time) {
        const merged = moment(date);
        merged.set({
          hour: time.hour(),
          minute: time.minute(),
        });
        this.maintenanceFormGroup.get('completionDate')?.setValue(merged);
      }

      const payload = {
        type: formValue.maintenanceNotificationType?.id,
        category: formValue.category?.id,
        errorCode: formValue.errorCode?.id,
        costCenter: formValue.costCenter?.id ?? null,
        title: formValue.title,
        description: formValue.comment,
        contactPerson: formValue.contactPerson || null,
        clientCreationDate: formValue.clientCreationDate?.toISOString(true),
        requiredCompletionDate: this.maintenanceFormGroup
          .get('completionDate')
          ?.value?.toISOString(true),
        bomNodeId: formValue.bomNodeId,
        domain: this.operationalArea(),
      };

      this.store.addMaintenanceNotification({
        payload: payload,
        files: this.filesToUpload,
        onSuccess: (createdEntry) => {
          this.store.setPage(0);
          this.matDialogRef.close();

          this.router.navigate(
            ['/', WHRouteZeroENUM.MAINTENANCE_NOTIFICATIONS],
            {
              queryParams: { id: createdEntry?.id },
            },
          );
        },
      });
    }
  }
}
